/**
 * Camera operator — headless "follow the edit" viewport intelligence.
 *
 * As the user edits the model (or changes selection), the operator decides
 * whether the viewport should move and, if so, where — like a camera operator
 * who *knows what just happened* rather than a reflex that snaps to a target on
 * every change. It is deliberately **pure and DOM-free**: it takes the current
 * viewport, the screen size, the solved space tree, and a normalised signal,
 * and returns a `stay` / `move` decision. No React, no rAF, no `performance.now`
 * — the browser layer feeds it timestamps and applies its decisions through the
 * existing animation loop. That keeps the whole behaviour unit-testable without
 * a browser (the layout/viewport abstractions are already pure JS; this joins
 * them).
 *
 * The behaviours it encodes (see TODO.md "Smart view"):
 *   - Edit magnitude scales motion. A tiny in-place value edit on a node that's
 *     already comfortably in frame → STAY (don't fly for fixing one symbol). A
 *     structural change (a node appeared / was reshaped) → bring it into frame.
 *   - Multi-touch → zoom out. Several nodes changed at once → frame the whole
 *     region (their bounding box), not chase one.
 *   - Selection → frame the set. Selecting N nodes frames their bounding box;
 *     selecting one gently reveals it if it's off-screen.
 *   - Manual override. A board gesture suppresses auto-motion for a short window
 *     (`noteUserNavigation`), and the browser cancels any in-flight animation
 *     the instant the user grabs the board.
 *   - Typing commitment. A value edit on an *off-screen* node doesn't fly on the
 *     very first keystroke — it waits until the node has been the edit focus for
 *     a couple of consecutive (debounced) signals, so a stray edit elsewhere
 *     doesn't yank the view.
 *
 * Framing math (`frameRect`, `boundingRect`, `rectVisibility`) and focal-path
 * helpers live in `viewport.ts`; this module is the decision policy on top.
 */
import type { BoardItem } from "./types.js";
import {
  ZOOM_MAX,
  ZOOM_MIN,
  asSource,
  boundingRect,
  focalRectsForPath,
  frameRect,
  rectVisibility,
  resolveFocal,
  type ItemLocalRect,
  type SpaceLike,
  type Viewport,
} from "./viewport.js";

/** What the operator needs to know about the world to make a decision. */
export interface OperatorScene {
  /** The live viewport (focal path + pan/zoom). */
  readonly viewport: Viewport;
  readonly screen: { readonly width: number; readonly height: number };
  /** The (possibly lazy) solved space tree. */
  readonly source: SpaceLike;
  /**
   * Resolve a node id to the focal path of the space that CONTAINS it (i.e. the
   * node is a direct child of `source.at(focalPathOf(id))`). Model-derived so it
   * reaches nodes below the lazy board's solved preview depth. Returns null when
   * the id is unknown.
   */
  readonly focalPathOf: (id: string) => readonly string[] | null;
  /**
   * Scope-dial expansion: map the raw edited id(s) to the target the current
   * altitude ring wants framed (the node, its neighbourhood, or an enclosing
   * space). Supplied by the binder (reads the live altitude); absent ⇒ frame the
   * ids as-is. Applied at frame time so a session's identity stays the RAW ids.
   */
  readonly expandTarget?: (ids: readonly string[]) => FrameTarget;
  /**
   * Where an in-flight operator animation is heading, if any. When a session
   * lazily captures `home` mid-animation, the camera is only *passing through*
   * `viewport` — the real rest is the animation's destination. Capturing that
   * instead avoids a later drift-back gliding to a transient spot the camera
   * never settled at. Cleared by a manual gesture (no stale-destination
   * resurrection after a pan). Absent ⇒ capture the live viewport.
   */
  readonly inFlightDest?: Viewport;
}

/** A frame target: either explicit board ids, or a whole space (by focal path).
 *  The scope dial's rings 3–4 produce space targets; everything else is ids. */
export type FrameTarget =
  | { readonly ids: readonly string[] }
  | { readonly spacePath: readonly string[] };

/**
 * A normalised input. The browser binder produces these from the model diff /
 * selection; the operator never sees raw editor or compiler state.
 *  - `edit`: `changedIds` are the nodes whose content changed since the last
 *    compile; `structural` is true when the symbol surface changed (a node was
 *    added / removed / renamed / re-parented) vs. a pure value edit.
 *  - `selection`: the nodes the user explicitly selected.
 * `at` is a monotonic timestamp (ms) injected by the caller — kept out of the
 * operator so it stays pure and deterministic in tests.
 */
export type OperatorSignal =
  | {
      readonly type: "edit";
      readonly changedIds: readonly string[];
      readonly structural: boolean;
      readonly at: number;
      /** True when `changedIds` is a process's spanned modules (frame the whole
       *  region), not a single edited node. */
      readonly region?: boolean;
    }
  | {
      readonly type: "selection";
      readonly ids: readonly string[];
      readonly at: number;
      /**
       * The camera is already HOLDING this target (the focus director framed it
       * earlier in the session) — use the tight gate: re-frame whenever the
       * ideal framing's zoom differs materially from the current zoom, in
       * EITHER direction. Without it, a framed node that shrinks (text
       * deleted) stays "fully inside" and the loose gate never zooms back in.
       */
      readonly tracking?: boolean;
      /**
       * Ignore the manual-gesture suppression window for THIS frame. Set only by
       * the spotlight hold: holding the key IS explicit user intent, so a recent
       * board gesture shouldn't mute it. (Manual control during a hold is
       * protected by the gesture LATCH in the binder, not by suppression.)
       */
      readonly bypassSuppression?: boolean;
      /**
       * Frame even while the operator is DISABLED. The explicit verbs (spotlight
       * hold, scope dial, frame-now) are an always-available "show me" gesture —
       * they work whether or not smart-view autopilot is toggled on. Auto signals
       * (edit/plain selection) never set it, so muting the toggle still stops all
       * automatic motion.
       */
      readonly bypassEnabled?: boolean;
    }
  | {
      /** Frame a whole space (the scope dial's rings 3–4, overview-peek). `path`
       *  is the space's focal path; the camera dives/pops to it and frames its
       *  contents. */
      readonly type: "space";
      readonly path: readonly string[];
      readonly at: number;
      readonly tracking?: boolean;
      readonly bypassSuppression?: boolean;
      readonly bypassEnabled?: boolean;
    };

export type OperatorStayReason =
  | "disabled"
  | "user-navigating"
  | "empty"
  | "unresolved"
  | "on-screen"
  | "await-commitment"
  | "cross-focal-value"
  /** The focus director is waiting out the quiet window before it settles. */
  | "settling";

export type OperatorMoveReason =
  | "structural"
  | "multi"
  | "selection"
  | "reveal"
  | "typing-followed"
  | "process-span"
  | "restore";

export type OperatorDecision =
  | { readonly kind: "stay"; readonly reason: OperatorStayReason }
  | {
      readonly kind: "move";
      readonly viewport: Viewport;
      readonly reason: OperatorMoveReason;
      /** True when the move stays in the current focal (a smooth lerp is valid);
       *  false for a cross-focal jump (lerping across coordinate spaces is
       *  meaningless — the browser jumps instead). */
      readonly animate: boolean;
    };

export interface OperatorTuning {
  /** After a user board gesture, suppress auto-motion for this long (ms). */
  readonly userNavSuppressMs: number;
  /** Consecutive value-edit signals on the same off-screen node before the
   *  operator follows it (the "don't fly on the first keystroke" guard). */
  readonly typingCommitCount: number;
  /** A framed/visible target must cover at least this fraction of the screen to
   *  count as comfortable (smaller ⇒ too far out, reframe). */
  readonly minCoverage: number;
  /** …and at most this fraction (larger ⇒ spilling past the edges, reframe). */
  readonly maxCoverage: number;
  /** Inset (px) for the "fully inside the screen" visibility test. */
  readonly visibilityMargin: number;
  /** Padding around a single framed target (fraction of the screen). */
  readonly framePad: number;
  /** Padding around an overview (multi) frame — looser, leaves breathing room. */
  readonly overviewPad: number;
  /**
   * Tracking gate (used when the camera already HOLDS the target): re-frame
   * once the ideal framing's zoom differs from the current zoom by more than
   * this (|ln(idealZoom / zoom)|). 0.2 ≈ ±22%. Symmetric, so a held node that
   * GROWS zooms out and one that SHRINKS (text deleted) zooms back in.
   */
  readonly trackZoomSlackLn: number;
}

export const DEFAULT_OPERATOR_TUNING: OperatorTuning = {
  userNavSuppressMs: 1200,
  typingCommitCount: 2,
  minCoverage: 0.04,
  maxCoverage: 0.78,
  visibilityMargin: 24,
  framePad: 0.2,
  overviewPad: 0.32,
  trackZoomSlackLn: 0.2,
};

/** A planned two-node frame — see `planPairFrame`. */
export interface PairFramePlan {
  /** The complete target viewport (focal path + rects + pan/zoom). */
  readonly viewport: Viewport;
  /** True when the plan stays in `current`'s focal — a smooth lerp is valid;
   *  false for a cross-focal jump (lerping across coordinate spaces is
   *  meaningless, set it immediately). */
  readonly sameFocal: boolean;
}

/**
 * Plan a viewport that shows a connection's two endpoint nodes together —
 * the step-scrubber's "locate this step on the board". Reuses the camera
 * operator's focal/representative machinery, with the connection-specific
 * ancestor rules:
 *
 *  - endpoints in different branches → framed from their common ancestor
 *    space (a deep endpoint is represented by its visible branch item, i.e.
 *    a connection reaching INTO a submodule is shown from OUTSIDE);
 *  - one endpoint an ancestor MODULE of the other (a child calling its own
 *    parent) → framed INSIDE the parent: the focal dives to the inner
 *    node's space, the parent is the visible focal frame around it;
 *  - same node on both ends (note steps) → a single-item frame.
 *
 * Pure: returns null when an endpoint can't be resolved (extern actor, lazy
 * source not materialised).
 */
export function planPairFrame(
  source: SpaceLike,
  aId: string,
  bId: string,
  current: Viewport,
  screen: { width: number; height: number },
  focalPathOf: (id: string) => readonly string[] | null,
  tuning: OperatorTuning = DEFAULT_OPERATOR_TUNING,
): PairFramePlan | null {
  const pathA = focalPathOf(aId);
  const pathB = focalPathOf(bId);
  if (!pathA || !pathB) return null;
  const targets: ResolvedTarget[] = [
    { id: aId, containingPath: pathA },
    { id: bId, containingPath: pathB },
  ];

  // Ancestor-module case ("show inside"): when one endpoint's own nested
  // space lies ON the other endpoint's containing path, the shallow endpoint
  // is a parent of the deep one — frame the deep node inside it instead of
  // collapsing both onto the parent's item from outside.
  let frameSpacePath: readonly string[] | null = null;
  let reps: BoardItem[] = [];
  const ancestorCase = (shallow: ResolvedTarget, deep: ResolvedTarget): boolean => {
    if (shallow.containingPath.length >= deep.containingPath.length) return false;
    if (!isPrefix(shallow.containingPath, deep.containingPath)) return false;
    const shallowItem = resolveFocal(source, shallow.containingPath).items.find(
      (it) => it.id === shallow.id,
    );
    const ownSpaceId = shallowItem?.space?.id;
    return ownSpaceId !== undefined && deep.containingPath[shallow.containingPath.length] === ownSpaceId;
  };
  const [ta, tb] = [targets[0]!, targets[1]!];
  const inside = ancestorCase(ta, tb) ? tb : ancestorCase(tb, ta) ? ta : null;
  if (inside) {
    frameSpacePath = inside.containingPath;
    const item = representativeItem(source, frameSpacePath, inside);
    if (item) reps = [item];
  } else {
    frameSpacePath = commonPrefix([pathA, pathB]);
    const seen = new Set<string>();
    for (const t of targets) {
      const item = representativeItem(source, frameSpacePath, t);
      if (item && !seen.has(item.id)) {
        seen.add(item.id);
        reps.push(item);
      }
    }
  }
  const box = boundingRect(reps);
  if (!box || !frameSpacePath) return null;

  const sameFocal = samePath(frameSpacePath, current.focalPath);
  const base: Viewport = {
    focalPath: frameSpacePath,
    focalRects: focalRectsForPath(source, frameSpacePath),
    center: { x: 0, y: 0 },
    zoom: 1,
  };
  // Looser pad when framing a pair (breathing room shows the connection);
  // the inside case pads generously too, so the parent frame reads around
  // the node.
  const pad = reps.length > 1 || inside ? tuning.overviewPad : tuning.framePad;
  const framed = clampFramed(frameRect(base, box, screen, pad), box, screen);
  return { viewport: framed, sameFocal };
}

/**
 * Would BOTH endpoints of a pair be fully visible under `vp`? The complement
 * of {@link planPairFrame}: step auto-framing asks it twice — "is the current
 * step already on screen?" (skip the move) and "does it fit the REMEMBERED
 * viewport?" (return home). Endpoints resolve to their representative items
 * at `vp.focalPath` level (a deep endpoint = its visible branch item); an
 * endpoint outside the focal subtree is not visible by definition.
 *
 * Returns null when an endpoint can't be resolved at all (extern actor,
 * lazy source) — the caller should then do nothing rather than move.
 */
export function pairFitsViewport(
  source: SpaceLike,
  aId: string,
  bId: string,
  vp: Viewport,
  screen: { width: number; height: number },
  focalPathOf: (id: string) => readonly string[] | null,
  margin = 0,
): boolean | null {
  const pathA = focalPathOf(aId);
  const pathB = focalPathOf(bId);
  if (!pathA || !pathB) return null;
  if (!isPrefix(vp.focalPath, pathA) || !isPrefix(vp.focalPath, pathB)) return false;
  const reps: BoardItem[] = [];
  const seen = new Set<string>();
  for (const t of [
    { id: aId, containingPath: pathA },
    { id: bId, containingPath: pathB },
  ]) {
    const item = representativeItem(source, vp.focalPath, t);
    if (!item) return null;
    if (!seen.has(item.id)) {
      seen.add(item.id);
      reps.push(item);
    }
  }
  const box = boundingRect(reps);
  if (!box) return null;
  return rectVisibility(vp, box, screen, margin).fullyInside;
}

export interface CameraOperator {
  /** Whether the operator is active. Mirrors the UI toggle. */
  enabled: boolean;
  setEnabled(on: boolean): void;
  /** Tell the operator the user just drove the board (pan/zoom/keys) at `at`,
   *  so it backs off for `userNavSuppressMs`. */
  noteUserNavigation(at: number): void;
  /** Decide what (if anything) to do for a signal against the current scene. */
  decide(signal: OperatorSignal, scene: OperatorScene): OperatorDecision;
  /** Forget edit history — call on a workspace swap so stale typing focus from
   *  the previous workspace can't influence the new one. */
  reset(): void;
}

const stay = (reason: OperatorStayReason): OperatorDecision => ({ kind: "stay", reason });

/** Longest common prefix of a set of focal paths. */
function commonPrefix(paths: readonly (readonly string[])[]): string[] {
  const first = paths[0];
  if (!first) return [];
  let prefix = [...first];
  for (let p = 1; p < paths.length; p++) {
    const path = paths[p]!;
    let i = 0;
    while (i < prefix.length && i < path.length && prefix[i] === path[i]) i++;
    prefix.length = i;
    if (i === 0) break;
  }
  return prefix;
}

/** True when `prefix` is a (non-strict) prefix of `path`. */
function isPrefix(prefix: readonly string[], path: readonly string[]): boolean {
  if (prefix.length > path.length) return false;
  for (let i = 0; i < prefix.length; i++) if (prefix[i] !== path[i]) return false;
  return true;
}

function samePath(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

interface ResolvedTarget {
  readonly id: string;
  /** Focal path of the space that directly contains the node. */
  readonly containingPath: readonly string[];
}

/**
 * The item in `frameSpace` (the space at `frameSpacePath`) that represents a
 * target — either the node itself (when the frame space directly contains it)
 * or the ancestor branch item whose nested space leads down to it. Lets a set
 * of targets scattered across nested spaces be framed together in their common
 * ancestor. Null when the path can't be walked (lazy source not materialised).
 */
function representativeItem(
  source: SpaceLike,
  frameSpacePath: readonly string[],
  target: ResolvedTarget,
): BoardItem | null {
  const space = resolveFocal(source, frameSpacePath);
  if (frameSpacePath.length === target.containingPath.length) {
    return space.items.find((it) => it.id === target.id) ?? null;
  }
  // Strict ancestor: the branch child is the item whose nested space id is the
  // next segment on the target's containing path.
  const branchSpaceId = target.containingPath[frameSpacePath.length];
  return space.items.find((it) => it.space?.id === branchSpaceId) ?? null;
}

/** Clamp a framed viewport's zoom into the rebasable range, keeping the rect
 *  centred. Stops the operator slamming to 5× on a tiny node or below the
 *  floor on a huge region. */
function clampFramed(
  vp: Viewport,
  rect: ItemLocalRect,
  screen: { width: number; height: number },
): Viewport {
  const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, vp.zoom));
  if (z === vp.zoom) return vp;
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  return { ...vp, zoom: z, center: { x: cx, y: cy } };
}

interface FrameOpts {
  readonly reason: OperatorMoveReason;
  /** May the operator change the focal path (pop out / dive) to show targets,
   *  or only pan/zoom within the current focal? Structural edits & selection
   *  may change focal (deliberate "show me"); value edits stay local. */
  readonly allowFocalChange: boolean;
  /** Frame the bounding region (overview padding) vs. a single target. */
  readonly multi: boolean;
  /** Comfort gate. "loose": stay while the target is fully visible within the
   *  coverage band (don't grab the camera). "tight": the camera already holds
   *  this target — stay only while the ideal framing is close to the current
   *  zoom, so a held node that grows OR shrinks gets re-framed. false: none. */
  readonly gentle: false | "loose" | "tight";
  /** Require typing commitment before following an off-screen single target. */
  readonly requireCommitment: boolean;
}

export function createCameraOperator(
  tuning: OperatorTuning = DEFAULT_OPERATOR_TUNING,
): CameraOperator {
  let enabled = false;
  let lastUserNavAt = -Infinity;
  // Typing focus: the node a run of single value-edits is concentrated on, and
  // how many consecutive signals it has held — drives the commitment gate.
  let focusId: string | null = null;
  let focusStreak = 0;

  const op: CameraOperator = {
    get enabled() {
      return enabled;
    },
    set enabled(on: boolean) {
      enabled = on;
    },
    setEnabled(on) {
      enabled = on;
    },
    noteUserNavigation(at) {
      lastUserNavAt = at;
    },
    reset() {
      focusId = null;
      focusStreak = 0;
      lastUserNavAt = -Infinity;
    },
    decide(signal, scene): OperatorDecision {
      const explicit = signal.type === "selection" || signal.type === "space";
      // Explicit verbs bypass the toggle — they're an always-available "show me"
      // gesture. Auto signals stay muted while disabled.
      if (!enabled && !(explicit && signal.bypassEnabled === true)) {
        return stay("disabled");
      }
      const bypass = explicit && signal.bypassSuppression === true;
      if (!bypass && signal.at - lastUserNavAt < tuning.userNavSuppressMs) {
        return stay("user-navigating");
      }

      if (signal.type === "space") {
        return frameSpace(signal.path, scene, signal.tracking === true);
      }

      if (signal.type === "selection") {
        if (signal.ids.length === 0) return stay("empty");
        const multi = signal.ids.length > 1;
        return frameTargets(signal.ids, scene, {
          reason: multi ? "selection" : "reveal",
          allowFocalChange: true,
          multi,
          gentle: signal.tracking ? "tight" : "loose",
          requireCommitment: false,
        });
      }

      // --- edit ---
      const ids = signal.changedIds;
      if (ids.length === 0) return stay("empty");

      if (signal.structural) {
        // A deliberate structural change — reset typing focus and show it.
        focusId = null;
        focusStreak = 0;
        const multi = ids.length > 1;
        return frameTargets(ids, scene, {
          reason: multi ? "multi" : "structural",
          allowFocalChange: true,
          multi,
          gentle: "loose",
          requireCommitment: false,
        });
      }

      // A pure value edit. Track typing focus so we only follow an off-screen
      // node once the user has committed to editing it.
      if (ids.length === 1) {
        const only = ids[0]!;
        if (focusId === only) focusStreak += 1;
        else {
          focusId = only;
          focusStreak = 1;
        }
      } else {
        focusId = null;
        focusStreak = 0;
      }
      return frameTargets(ids, scene, {
        reason: "typing-followed",
        // Value edits never change focal — they pan/zoom gently within the
        // current focal, or stay if the target isn't reachable there.
        allowFocalChange: false,
        multi: ids.length > 1,
        gentle: "loose",
        requireCommitment: ids.length === 1,
      });
    },
  };

  function frameTargets(
    ids: readonly string[],
    scene: OperatorScene,
    opts: FrameOpts,
  ): OperatorDecision {
    const { source, viewport, screen } = scene;

    // Resolve ids → containing focal paths, dropping anything unknown.
    const targets: ResolvedTarget[] = [];
    for (const id of ids) {
      const containingPath = scene.focalPathOf(id);
      if (containingPath) targets.push({ id, containingPath });
    }
    if (targets.length === 0) return stay("unresolved");

    const paths = targets.map((t) => t.containingPath);
    const common = commonPrefix(paths);

    // Decide which space to frame in.
    let frameSpacePath: readonly string[];
    if (opts.allowFocalChange) {
      frameSpacePath = common;
    } else {
      // Value edit: only showable if every target sits at/below the current
      // focal (so a pure pan/zoom can reveal it). Otherwise leave the view be.
      if (!paths.every((p) => isPrefix(viewport.focalPath, p))) {
        return stay("cross-focal-value");
      }
      frameSpacePath = viewport.focalPath;
    }

    // Collect the representative items in the frame space and their box.
    const reps: BoardItem[] = [];
    for (const t of targets) {
      const item = representativeItem(source, frameSpacePath, t);
      if (item) reps.push(item);
    }
    const box = boundingRect(reps);
    if (!box) return stay("unresolved");

    const sameFocal = samePath(frameSpacePath, viewport.focalPath);
    const pad = opts.multi ? tuning.overviewPad : tuning.framePad;

    // Already-comfortable gate (only meaningful in the current focal's coords).
    if (opts.gentle !== false && sameFocal) {
      const vis = rectVisibility(viewport, box, screen, tuning.visibilityMargin);
      if (vis.fullyInside) {
        if (opts.gentle === "tight") {
          // The camera already holds this target: stay only while the ideal
          // framing is close to the current zoom. Symmetric — a held node that
          // GROWS (zoom would drop) or SHRINKS (zoom would rise, e.g. text
          // deleted) both re-frame once past the slack.
          const ideal = clampFramed(frameRect(viewport, box, screen, pad), box, screen);
          if (Math.abs(Math.log(ideal.zoom / viewport.zoom)) <= tuning.trackZoomSlackLn) {
            return stay("on-screen");
          }
        } else if (vis.coverage >= tuning.minCoverage && vis.coverage <= tuning.maxCoverage) {
          return stay("on-screen");
        }
      }
    }

    // Don't chase an off-screen single node on the first keystroke — wait until
    // the user has committed to editing it.
    if (opts.requireCommitment && focusStreak < tuning.typingCommitCount) {
      return stay("await-commitment");
    }

    const base: Viewport = sameFocal
      ? viewport
      : {
          focalPath: frameSpacePath,
          focalRects: focalRectsForPath(source, frameSpacePath),
          center: { x: 0, y: 0 },
          zoom: 1,
        };
    const framed = clampFramed(frameRect(base, box, screen, pad), box, screen);
    return { kind: "move", viewport: framed, reason: opts.reason, animate: sameFocal };
  }

  /** Frame a whole space (scope-dial rings 3–4). Same gating as `frameTargets`
   *  (enabled + suppression handled in `decide`; comfortable gate here), but the
   *  box is every item in the space and the frame always uses overview padding. */
  function frameSpace(
    path: readonly string[],
    scene: OperatorScene,
    tracking: boolean,
  ): OperatorDecision {
    const { source, viewport, screen } = scene;
    const space = resolveFocal(source, path);
    const box = boundingRect(space.items);
    if (!box) return stay("unresolved");
    const sameFocal = samePath(path, viewport.focalPath);
    const pad = tuning.overviewPad;
    if (sameFocal) {
      const vis = rectVisibility(viewport, box, screen, tuning.visibilityMargin);
      if (vis.fullyInside) {
        if (tracking) {
          const ideal = clampFramed(frameRect(viewport, box, screen, pad), box, screen);
          if (Math.abs(Math.log(ideal.zoom / viewport.zoom)) <= tuning.trackZoomSlackLn) {
            return stay("on-screen");
          }
        } else if (vis.coverage >= tuning.minCoverage && vis.coverage <= tuning.maxCoverage) {
          return stay("on-screen");
        }
      }
    }
    const base: Viewport = sameFocal
      ? viewport
      : {
          focalPath: path,
          focalRects: focalRectsForPath(source, path),
          center: { x: 0, y: 0 },
          zoom: 1,
        };
    const framed = clampFramed(frameRect(base, box, screen, pad), box, screen);
    return { kind: "move", viewport: framed, reason: "selection", animate: sameFocal };
  }

  return op;
}
